import { AbstractViewModel, useViewModel } from '@footsteps/shared';
import type { PdokAddressDocResult } from '@footsteps/types';
import { addressFormSchema } from './woz-find-address-modal.schema';
import { SupabaseApiService } from '../../services/supabase-api';
import type { AddressFormValues, AddressFormErrors, WozFindAddressModalState } from './woz-find-address-modal.types';

export class WozFindAddressModalViewModel extends AbstractViewModel<WozFindAddressModalState> {
  static initialState: WozFindAddressModalState = {
    postalCode: '',
    houseNumber: '',
    houseNumberAddition: '',
    errors: {},
    isLoading: false,
    apiError: null,
    results: [],
    selectedAddress: null,
    needsSelection: false,
    canConfirm: false,
  };

  constructor() {
    super(WozFindAddressModalViewModel.initialState);
  }

  setPostalCode = (value: string): void => {
    this.update((state) => {
      state.postalCode = value;
    });
  };

  setHouseNumber = (value: string): void => {
    this.update((state) => {
      state.houseNumber = value;
    });
  };

  setHouseNumberAddition = (value: string): void => {
    this.update((state) => {
      state.houseNumberAddition = value;
    });
  };

  // ── Select an address from results ─────────────────────────────────────

  selectAddress = (address: PdokAddressDocResult): void => {
    this.update((state) => {
      state.selectedAddress = address;
      state.canConfirm = true;
    });
  };

  // ── State helpers ──────────────────────────────────────────────────────

  private setIsLoading(isLoading: boolean) {
    this.update((state) => {
      state.isLoading = isLoading;
      state.apiError = null;
    });
  }

  private setError(apiError: string) {
    this.update((state) => {
      state.apiError = apiError;
      state.isLoading = false;
      state.results = [];
      state.selectedAddress = null;
      state.needsSelection = false;
      state.canConfirm = false;
    });
  }

  // ── Search ─────────────────────────────────────────────────────────────

  handleSearch = async (): Promise<void> => {
    const { postalCode, houseNumber, houseNumberAddition } = this.state.getValue();

    this.update((state) => {
      state.errors = {};
      state.apiError = null;
      state.selectedAddress = null;
      state.canConfirm = false;
    });

    // Validate with Zod
    const parsed = addressFormSchema.safeParse({ postalCode, houseNumber, houseNumberAddition });
    if (!parsed.success) {
      const fieldErrors: AddressFormErrors = {};
      for (const issue of parsed.error.issues) {
        const field = issue.path[0] as keyof AddressFormValues;
        fieldErrors[field] = issue.message;
      }
      this.update((state) => {
        state.errors = fieldErrors;
      });
      return;
    }

    this.setIsLoading(true);

    try {
      const { postalCode: pc, houseNumber: hn, houseNumberAddition: hna } = parsed.data;
      const api = SupabaseApiService.getInstance();
      const response = await api.lookupAddress(pc, hn, hna || undefined);

      if (response.status === 'not_found' || response.addresses.length === 0) {
        this.setError('Geen adres gevonden. Controleer de invoer en probeer opnieuw.');
        return;
      }

      this.update((state) => {
        state.isLoading = false;
        state.results = response.addresses;
        state.needsSelection = response.addresses.length > 1;

        // Auto-select if only one result
        if (response.addresses.length === 1) {
          state.selectedAddress = response.addresses[0];
          state.canConfirm = true;
        }
      });
    } catch (err) {
      this.setError(err instanceof Error ? err.message : 'Er is een fout opgetreden.');
    }
  };

  handleConfirm = (onConfirm: (addressData: Record<string, unknown>) => void): void => {
    const { selectedAddress } = this.state.getValue();
    if (!selectedAddress) return;

    onConfirm({
      addressPointId: selectedAddress.addressPointId ?? selectedAddress.id ?? '',
      addressCity: selectedAddress.city ?? '',
      addressStreet: selectedAddress.street ?? '',
      addressHouseNumber: Number(selectedAddress.houseNumber ?? 0),
      addressHouseLetter: null,
      addressHouseNumberAddition: selectedAddress.houseNumberAddition ?? null,
      addressPostalCode: selectedAddress.postalCode ?? '',
    });
  };
}

export const useWozFindAddressModalViewModel = () =>
  useViewModel<WozFindAddressModalState, WozFindAddressModalViewModel>(() => new WozFindAddressModalViewModel());
